import { store } from '../state/store';
import { saveFileDownload } from '../utils/fileDownloadStorage';
import { isWidgetAuthExpiredError, webchatApi } from './api';
import { activateCharts } from './stream';
import { awaitChatResponse, readChatResponse } from './turn-stream-fallback';
import type { ChatMessage, FileAttachment, TurnState, LlmMessage, LlmContentBlock } from '../types';
import { t } from '../i18n';
import { generateId } from '../components/inputBar/helpers';
import { normalizeMisleadingFormMention } from '../utils/form-renderer';
import { canonicalArgsHash } from '../utils/tool-routing-metadata.util';
import {
  appendUserToHistory,
  seedHistoryIfEmpty,
  setStructuredHistory,
} from '../utils/historyMessages';
import { getTtsService } from './tts';
import { restoreSpeechPunctuation } from '../utils/speechPunctuation';
import { normalizeSpokenDigitSequences } from '../utils/spokenDigits';
import { enableAutoScroll } from '../utils/scroll';
import { ChatOrchestratorBase, CHAT_TIMEOUT_MS } from './orchestrator-fuctions/orchestrator-base';
import { withContextEnrichment } from './orchestrator-fuctions/context-enrichment';
import { withToolIndicatorDom } from './orchestrator-fuctions/tool-indicator-dom';
import { withToolsHandling } from './orchestrator-fuctions/tools-handle';
import { withConfirmationHandling } from './orchestrator-fuctions/confirmation-handle';
import { withTurnHandling } from './orchestrator-fuctions/turn-handle';

// Composition order matters: withConfirmationHandling calls this.clearTurn/
// this.handleTurnTimeout via stubs that must be overridden by the REAL
// implementations in withTurnHandling, which requires withTurnHandling to be
// the more-derived (outer) mixin — see confirmation-handle.ts's docblock.
export class ChatOrchestrator extends withTurnHandling(
  withConfirmationHandling(
    withToolsHandling(
      withToolIndicatorDom(
        withContextEnrichment(ChatOrchestratorBase)
      )
    )
  )
) {
  private buildAssistantErrorContent(err: unknown, fallbackMessage: string, accumulatedText: string): string {
    const errorMessage = err instanceof Error && err.message.trim()
      ? err.message.trim()
      : fallbackMessage;
    const partialText = accumulatedText.trim();

    if (!partialText) {
      return errorMessage;
    }

    if (partialText === errorMessage) {
      return partialText;
    }

    return `${partialText}\n\n[[ ${errorMessage} ]]`;
  }

  async sendMessage(userText: string, attachments: FileAttachment[] = [], sttUsed = false): Promise<void> {
    // Block if widget is inactive
    if (store.getState().widgetInactive) return;
    enableAutoScroll(this.shadow.querySelector('.panel-body') as HTMLDivElement | null);

    const locale = store.getState().config.language || 'he';
    const normalizedUserText = sttUsed
      ? restoreSpeechPunctuation(normalizeSpokenDigitSequences(userText.trim()), locale)
      : userText.trim();

    if (!normalizedUserText && attachments.length === 0) {
      return;
    }

    const sourceConversationId = store.getState().conversationId;
    const clientChatId = store.getState().clientChatId;

    // Safety net: never allow a second concurrent turn against the same real
    // conversation. Brand-new (sourceConversationId === null) sends are always
    // distinct from each other - there's no shared identity to collide on until
    // the backend assigns a real conversationId.
    if (sourceConversationId !== null) {
      for (const existing of this.turns.values()) {
        if (existing.sourceConversationId === sourceConversationId) {
          console.warn(`[mcp-chat-widget] Conversation ${sourceConversationId} already has an active turn - ignoring send`);
          return;
        }
      }
    } else {
      for (const existing of this.turns.values()) {
        if (existing.sourceConversationId === null) {
          console.warn('[mcp-chat-widget] A turn is already in progress - ignoring send until it completes');
          return;
        }
      }
    }

    seedHistoryIfEmpty();

    // 1. Add user message to store (synchronous, pre-await - always the live view)
    const userMsg: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: normalizedUserText,
      timestamp: Date.now(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };
    store.addMessage(userMsg);
    appendUserToHistory(normalizedUserText);

    // 2. Reset driving mode state BEFORE adding the streaming placeholder so that
    //    the DrivingMode subscription (which fires synchronously on store.addMessage)
    //    sees an empty lastResponse and doesn't feed the previous turn's text to TTS.
    if (store.getState().drivingMode.active) {
      getTtsService().stop();
      store.setDrivingMode({
        lastResponse: '',
        speaking: false,
        error: null,
      });
    }

    // 3. Create streaming assistant placeholder
    const assistantId = generateId();
    store.addMessage({
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isStreaming: true,
    });
    store.setState({ isLoading: true });
    store.clearToolCalls();

    const turnId = generateId();
    const turn: TurnState = {
      turnId,
      sourceConversationId,
      resolvedConversationId: sourceConversationId,
      clientChatId,
      assistantId,
      accumulatedText: '',
      streamFlushHandle: null,
      abortController: new AbortController(),
      timeoutHandle: setTimeout(() => this.handleTurnTimeout(turnId), CHAT_TIMEOUT_MS),
      interrupted: false,
      abortReason: null,
      clientRoundTripCount: 0,
      failedNonRetryableCalls: new Set<string>(),
      succeededCallResults: new Map<string, string>(),
    };
    this.turns.set(turnId, turn);
    // Bind synchronously, in the same call stack that created the turn - no race
    // with the mcp-chat:message-sent DOM event or any listener.
    store.setState({ boundTurnId: turnId });
    this.drivingResponseTracker.reset();

    // Dispatch event
    (this.shadow.host as HTMLElement).dispatchEvent(
      new CustomEvent('mcp-chat:message-sent', {
        bubbles: true,
        composed: true,
        detail: {
          turnId,
          message: normalizedUserText,
          sourceConversationId,
          assistantId,
          // Real id/attachments of the ChatMessage already added to the store above -
          // lets recovery/reconstruction reuse this exact message instead of
          // synthesizing a lookalike one later (see Portal.tsx pending-turn handling).
          userMessageId: userMsg.id,
          attachments,
        },
      })
    );

    const ts = this.turnScopedStore(turnId);

    try {
      // 3. Wait for MCP tools to be ready (if still initializing)
      if (this.mcpReadyPromise) {
        await this.mcpReadyPromise;
      }

      // 4. Build messages array from the canonical structured LLM history (includes
      // tool_use/tool_result blocks from earlier turns, not just display text).
      const messages = this.getHistoryMessages();

      // 4. Get tool definitions from MCP discovery, filtered by allowedTools config
      const tools = this.getFilteredTools();
      const { dangerousTools } = store.getState().config;

      // 5. Send to /webchat/chat with tools + dangerous tool IDs. A null response means its
      // headers never arrived (buffering proxy) - readChatStream then polls the run instead.
      const response = await awaitChatResponse(webchatApi.chat(
        messages,
        tools.length > 0 ? tools : undefined,
        sourceConversationId || undefined,
        clientChatId,
        dangerousTools && dangerousTools.length > 0 ? dangerousTools : undefined,
        sttUsed,
        turn.abortController.signal,
        turn.clientRoundTripCount,
        turn.turnId,
      ));

      if (response && !response.ok) {
        if (response.status === 429) {
          let retryAfterMs = 30_000;
          let captchaRequired = false;
          try {
            const body = await response.json();
            if (body.retryAfterMs) retryAfterMs = body.retryAfterMs;
            if (body.captchaRequired) captchaRequired = true;
          } catch { /* use default */ }
          throw Object.assign(
            new Error(t('rateLimited') || 'Please wait before sending another message.'),
            { isRateLimited: true, retryAfterMs, captchaRequired },
          );
        }
        let errorDetail = `Chat failed: ${response.status}`;
        try {
          const errBody = await response.text();
          console.error('[mcp-chat-widget] Chat error response:', errBody);
          errorDetail += ` - ${errBody}`;
        } catch { /* ignore */ }
        throw new Error(errorDetail);
      }

      // 6. Read SSE stream with full event handling
      await this.readChatStream(response, turn, undefined, turn.clientRoundTripCount);

      // Turn (including any nested tool-call round trips) has fully completed
      // and the conversation has been persisted server-side.
      (this.shadow.host as HTMLElement).dispatchEvent(
        new CustomEvent('mcp-chat:message-received', {
          bubbles: true,
          composed: true,
          detail: { turnId, conversationId: turn.resolvedConversationId },
        })
      );
    } catch (err) {
      if (turn.abortReason === 'user' || turn.abortReason === 'timeout') {
        // 'user': today's silent Stop behavior. 'timeout': mcp-chat:turn-timeout
        // already dispatched by handleTurnTimeout - don't also fire mcp-chat:error.
        return;
      }

      if (isWidgetAuthExpiredError(err)) {
        ts.removeMessage(assistantId);
        ts.setState({ isLoading: false });
        ts.requestInputFocus();
        return;
      }

      const isRateLimited = (err as any)?.isRateLimited === true;
      this.cancelStreamingContentFlush(turn);
      ts.updateMessage(assistantId, {
        content: isRateLimited
          ? (t('rateLimited') || 'Please wait a moment before sending another message.')
          : this.buildAssistantErrorContent(
            err,
            t('chatError') || 'Sorry, an error occurred. Please try again.',
            turn.accumulatedText,
          ),
        isStreaming: false,
        ...(isRateLimited ? {} : { turnError: { turnId } }),
      });

      if (isRateLimited) {
        const lockMs = (err as any).retryAfterMs || 30_000;
        const captchaNeeded = (err as any).captchaRequired === true;
        ts.setState({
          isLoading: false,
          rateLimitLock: {
            lockedUntil: Date.now() + lockMs,
            captchaRequired: captchaNeeded,
          },
        });
      } else {
        ts.setState({ isLoading: false });
        ts.requestInputFocus();
        const message = err instanceof Error ? err.message : 'Unknown error';
        const errorDetail = (err as any)?.detail || null;
        (this.shadow.host as HTMLElement).dispatchEvent(
          new CustomEvent('mcp-chat:error', {
            bubbles: true,
            composed: true,
            detail: { turnId, error: message, technicalDetail: errorDetail },
          })
        );
      }
    } finally {
      this.clearTurn(turnId);
    }
  }

  /**
   * `pollRoundTrip` is set for /webchat/chat responses (the server records those runs by
   * turnId + round trip), enabling the polling fallback when the stream goes silent; `response`
   * may then be null. Confirm-endpoint responses aren't recorded, so they pass neither.
   */
  protected async readChatStream(
    response: Response | null,
    turn: TurnState,
    conversationMessages?: LlmMessage[],
    pollRoundTrip?: number,
  ): Promise<void> {
    const { turnId, assistantId } = turn;
    const ts = this.turnScopedStore(turnId);
    // Track tool_use events from the server (client tool execution mode)
    const pendingToolUses: Array<{ id: string; name: string; input: Record<string, unknown> }> = [];
    const toolResults: LlmMessage[] = [];
    let autoApprovalToken: string | null = null;
    // Any tool_use this turn (client- or server-executed) means the model was
    // acting on a specific tool, so a stray "form" mention in its prose isn't
    // the ambiguous-intent failure mode normalizeMisleadingFormMention guards against.
    let toolUseOccurredThisTurn = false;
    // Read before writing so per-token text deltas don't each re-render the bubble
    const clearAnalystProgress = () => {
      if (store.getState().messages.find((m) => m.id === assistantId)?.analystProgress) {
        ts.updateMessage(assistantId, { analystProgress: null });
      }
    };
    this.drivingResponseTracker.reset();
    const pollTarget = pollRoundTrip != null
      ? { turnId: turn.turnId, roundTrip: pollRoundTrip, signal: turn.abortController.signal }
      : undefined;
    return new Promise<void>((resolve, reject) => {
      readChatResponse(response, {
        onEvent: (parsed) => {
          this.resetTurnTimeout(turn);
          const drivingText = this.drivingResponseTracker.extractDrivingResponseText(parsed);
          if (!drivingText) return;
          this.drivingResponseTracker.updateLastResponseFromDriving(drivingText);
        },

        onConversationId: (id) => {
          this.resetTurnTimeout(turn);
          turn.resolvedConversationId = id;
          ts.setState({ conversationId: id });
        },

        onIterationUsage: (iteration, totalTokens, tools, llmRequestPayload, cacheReadTokens, meta) => {
          this.resetTurnTimeout(turn);
          const current = store.getState().messages.find((m) => m.id === assistantId);
          const existing = current?.iterationTokens ?? [];
          ts.updateMessage(assistantId, {
            iterationTokens: [
              ...existing,
              { iteration, totalTokens, tools, llmRequestPayload, cacheReadTokens, createdAt: new Date().toISOString(), ...meta },
            ],
          });
        },

        onHistorySummarization: (turnId, result) => {
          this.resetTurnTimeout(turn);
          const current = store.getState().messages.find((m) => m.id === assistantId);
          const existing = current?.summarizationEvents ?? [];
          ts.updateMessage(assistantId, {
            summarizationEvents: [...existing, { turnId, ...result }],
          });
        },

        onAnalystProgress: (progress) => {
          // Also keeps a long analyst run from tripping the client-side turn timeout
          this.resetTurnTimeout(turn);
          ts.updateMessage(assistantId, { analystProgress: progress });
        },

        onTextDelta: (_text, accumulated) => {
          this.resetTurnTimeout(turn);
          clearAnalystProgress();
          turn.accumulatedText = accumulated;
          this.drivingResponseTracker.updateLastResponseFromRaw(accumulated);
          // Store is the single source of truth for streamed content - batched to one
          // write per animation frame so React doesn't re-render per token. ts.updateMessage
          // already no-ops when this turn isn't the one bound to the visible UI.
          this.scheduleStreamingContentFlush(turn, ts, assistantId);
        },

        onToolCallStart: (id, name, input) => {
          this.resetTurnTimeout(turn);
          toolUseOccurredThisTurn = true;
          ts.addToolCall({ id, name, status: 'calling', inputs: input });
          if (!this.isSystemTool(name) && store.getState().boundTurnId === turnId) {
            this.appendToolIndicator(assistantId, id, this.resolveToolDisplayName(name), input);
          }
        },

        onToolCallResult: (id, _name, result, error) => {
          this.resetTurnTimeout(turn);
          if (_name === 'Data_Analyst') clearAnalystProgress();
          ts.updateToolCall(id, {
            status: error ? 'error' : 'done',
            result: result ? (typeof result === 'string' ? result : JSON.stringify(result)) : undefined,
            error: error || undefined,
          });
          if (store.getState().boundTurnId === turnId) {
            this.updateToolIndicator(id, error ? 'error' : 'done', error || undefined);
          }
        },

        onToolUse: (id, name, input) => {
          this.resetTurnTimeout(turn);
          toolUseOccurredThisTurn = true;
          pendingToolUses.push({ id, name, input });
          // tool_call_start already added the indicator for this id - skip duplicate
          const existing = store.getState().toolCalls.find(tc => tc.id === id);
          if (!existing) {
            ts.addToolCall({ id, name, status: 'calling', inputs: input });
            if (!this.isSystemTool(name) && store.getState().boundTurnId === turnId) {
              this.appendToolIndicator(assistantId, id, this.resolveToolDisplayName(name), input);
            }
          } else {
            ts.updateToolCall(id, { inputs: input });
          }
        },

        onFileDownload: (data) => {
          this.resetTurnTimeout(turn);
          console.debug(`[mcp-chat-widget] File download: ${data.filename} (${data.size} bytes)`);
          const file = {
            filename: data.filename,
            url: data.url,
            contentType: data.contentType,
            size: data.size,
          };
          ts.addFileToMessage(assistantId, file);
          // Persist across sessions - store with the current message index and a 24h expiry
          try {
            const conversationId = turn.resolvedConversationId;
            if (conversationId) {
              const msgIndex = store.getState().messages.findIndex(m => m.id === assistantId);
              const expiresAt = (data as any).expiresAt ?? new Date(Date.now() + 86_400_000).toISOString();
              saveFileDownload(conversationId, msgIndex, file, expiresAt);
            }
          } catch { /* ignore */ }
        },

        onConfirmationRequired: (token, toolName, toolArgs, ttlMs) => {
          this.resetTurnTimeout(turn);
          toolUseOccurredThisTurn = true;
          if (this.approvedTools.has(toolName)) {
            autoApprovalToken = token;
            return;
          }
          this.showConfirmationForm(assistantId, token, toolName, toolArgs, ttlMs);
        },

        onDone: async (accumulated, reason) => {

          if (reason === 'paused_for_confirmation' && autoApprovalToken) {
            const token = autoApprovalToken;
            autoApprovalToken = null;
            this.cancelStreamingContentFlush(turn);
            ts.updateMessage(assistantId, { content: accumulated, isStreaming: true });
            try {
              const nextResponse = await webchatApi.confirmToolCall(token, true);
              if (!nextResponse.ok) {
                throw new Error(`Auto-confirm failed: ${nextResponse.status}`);
              }
              await this.readChatStream(nextResponse, turn, conversationMessages);
              resolve();
            } catch (err) {
              if (turn.abortReason === 'user' || turn.abortReason === 'timeout') { reject(err); return; }
              this.cancelStreamingContentFlush(turn);
              ts.updateMessage(assistantId, {
                content: this.buildAssistantErrorContent(err, 'Auto-approval failed.', turn.accumulatedText),
                isStreaming: false,
              });
              ts.setState({ isLoading: false });
              ts.requestInputFocus();
              reject(err);
            }
            return;
          }

          if (reason === 'awaiting_tool_results' && pendingToolUses.length > 0) {
            const formEnabledForPending = this.isFormEnabledForTools(pendingToolUses.map((tu) => tu.name));

            // If the response contains a form block, the AI violated the rule of not
            // calling tools while presenting a form. Honour the form - skip tool
            // execution so the user can fill in the inputs first.
            if (formEnabledForPending && accumulated.includes('```form')) {
              console.debug('[mcp-chat-widget] Form block detected alongside tool use - skipping tool execution to let user fill form');
              this.cancelStreamingContentFlush(turn);
              ts.updateMessage(assistantId, { content: accumulated, isStreaming: false });
              ts.clearToolCalls();
              setTimeout(() => {
                ts.setState({ isLoading: false });
                ts.requestInputFocus();
              }, 500);
              resolve();
              return;
            }

            const missingFieldsContent = formEnabledForPending
              ? this.buildMissingRequiredFormContent(pendingToolUses)
              : this.buildMissingRequiredTextPrompt(pendingToolUses, accumulated);
            if (missingFieldsContent) {
              this.removeToolIndicators(pendingToolUses.map((toolUse) => toolUse.id));
              this.cancelStreamingContentFlush(turn);
              ts.updateMessage(assistantId, {
                content: missingFieldsContent,
                isStreaming: false,
                awaitingInput: true,
              });
              ts.clearToolCalls();
              ts.setState({ isLoading: false });
              ts.requestInputFocus();
              resolve();
              return;
            }

            // Client tool execution mode: execute each tool via MCP
            try {
              for (const tu of pendingToolUses) {
                console.debug(`[mcp-chat-widget] Executing tool via MCP: ${tu.name}`);
                let resultStr: string;
                let isError: boolean;
                try {
                  if (!this.isAllowedToolName(tu.name)) {
                    throw new Error(`Tool "${tu.name}" is not active for this widget`);
                  } else {
                    const enrichedInput = this.enrichToolInputFromContext(tu.name, tu.input);
                    const failKey = `${tu.name}:${canonicalArgsHash(enrichedInput)}`;
                    if (turn.failedNonRetryableCalls.has(failKey)) {
                      resultStr = JSON.stringify({
                        success: false,
                        code: 'IDENTICAL_TOOL_CALL_ALREADY_FAILED',
                        retryable: false,
                        message: 'This exact tool call already failed. Change the arguments before retrying.',
                      });
                      isError = true;
                    } else if (turn.succeededCallResults.has(failKey)) {
                      // Same tool + same arguments already succeeded earlier THIS turn. Re-serve the
                      // prior result instead of re-executing, and tell the model explicitly to stop -
                      // otherwise nothing prevents it from calling the same tool every round until the
                      // client round-trip cap kicks in (webchat_max_client_tool_round_trips).
                      resultStr = JSON.stringify({
                        success: true,
                        code: 'IDENTICAL_TOOL_CALL_ALREADY_SUCCEEDED',
                        message: 'This exact tool call already succeeded earlier in this turn. Do NOT call it again with the same arguments - use the data already returned below to answer the user now.',
                        previousResult: JSON.parse(turn.succeededCallResults.get(failKey)!),
                      });
                      isError = false;
                    } else {
                      const mcpResult = await this.mcpClient.callTool(tu.name, enrichedInput);
                      isError = mcpResult.isError || false;
                      const contentBlocks = isError
                        ? (mcpResult.content as any[]).map((item: any) => ({ ...item, isError: true }))
                        : mcpResult.content;
                      resultStr = JSON.stringify(contentBlocks);
                      if (isError) {
                        turn.failedNonRetryableCalls.add(failKey);
                      } else {
                        turn.succeededCallResults.set(failKey, resultStr);
                      }
                    }
                  }
                } catch (toolErr) {
                  // Individual tool failure - report error but continue the conversation
                  const errMsg = toolErr instanceof Error ? toolErr.message : String(toolErr);
                  console.error(`[mcp-chat-widget] Tool "${tu.name}" execution failed:`, errMsg);
                  resultStr = JSON.stringify({ error: errMsg });
                  isError = true;
                }

                // A tool call just returned (success or failure) - that's real progress,
                // even though it involved no SSE traffic while awaiting it.
                this.resetTurnTimeout(turn);

                ts.updateToolCall(tu.id, {
                  status: isError ? 'error' : 'done',
                  result: isError ? undefined : resultStr,
                  error: isError ? resultStr : undefined,
                });
                if (store.getState().boundTurnId === turnId) {
                  this.updateToolIndicator(tu.id, isError ? 'error' : 'done', isError ? resultStr : undefined);
                }

                toolResults.push({
                  role: 'tool',
                  content: resultStr,
                  tool_call_id: tu.id,
                });
              }

              const currentMsg = store.getState().messages.find((m) => m.id === assistantId);
              const iterationTokens = currentMsg?.iterationTokens ?? [];
              if (iterationTokens.length > 0 && pendingToolUses.length > 0) {
                const lastIdx = iterationTokens.length - 1;
                const lastEntry = iterationTokens[lastIdx];
                const executedTools = pendingToolUses.map((tu) => {
                  const enrichedInput = this.enrichToolInputFromContext(tu.name, tu.input);
                  const tc = store.getState().toolCalls.find((call) => call.id === tu.id);
                  return {
                    toolName: tu.name,
                    success: tc?.status !== 'error',
                    argsSummary: JSON.stringify(enrichedInput),
                    resultSummary: tc?.result ?? tc?.error,
                  };
                });
                const updated = [...iterationTokens];
                updated[lastIdx] = { ...lastEntry, tools: executedTools };
                ts.updateMessage(assistantId, { iterationTokens: updated });
              }

              // Build the continuation messages:
              // previous conversation + assistant message (with tool_use blocks) + tool results
              const baseMessages = conversationMessages || this.getHistoryMessages();

              // The assistant message MUST include tool_use content blocks
              // so the Anthropic API can match them to the tool_result messages
              const assistantContent: LlmContentBlock[] = [];
              if (accumulated) {
                assistantContent.push({ type: 'text', text: accumulated });
              }
              for (const tu of pendingToolUses) {
                const enrichedInput = this.enrichToolInputFromContext(tu.name, tu.input);
                assistantContent.push({
                  type: 'tool_use',
                  id: tu.id,
                  name: tu.name,
                  input: enrichedInput,
                });
              }
              const assistantMsg: LlmMessage = {
                role: 'assistant',
                content: assistantContent,
              };

              const continuation: LlmMessage[] = [...baseMessages, assistantMsg, ...toolResults];

              // Persist the structured history now (not just used transiently for this
              // recursive request) so the next top-level turn - and the LLM itself, if this
              // turn takes another tool round trip - can see this tool_use/tool_result pair.
              setStructuredHistory(continuation);

              console.debug(`[mcp-chat-widget] Resending with ${toolResults.length} tool results`);

              // Show the pre-tool-call prose while the continuation round starts.
              this.cancelStreamingContentFlush(turn);
              ts.updateMessage(assistantId, { content: accumulated, isStreaming: true });

              turn.clientRoundTripCount += 1;
              const tools = this.getFilteredTools();
              const nextResponse = await awaitChatResponse(webchatApi.chat(
                continuation,
                tools.length > 0 ? tools : undefined,
                turn.resolvedConversationId || undefined,
                turn.clientChatId,
                undefined,
                undefined,
                turn.abortController.signal,
                turn.clientRoundTripCount,
                turn.turnId,
              ));

              if (nextResponse && !nextResponse.ok) {
                if (nextResponse.status === 429) {
                  let retryAfterMs = 30_000;
                  let captchaRequired = false;
                  try {
                    const body = await nextResponse.json();
                    if (body.retryAfterMs) retryAfterMs = body.retryAfterMs;
                    if (body.captchaRequired) captchaRequired = true;
                  } catch { /* use default */ }
                  throw Object.assign(
                    new Error(t('rateLimited') || 'Please wait before sending another message.'),
                    { isRateLimited: true, retryAfterMs, captchaRequired },
                  );
                }
                let errorDetail = `Chat continuation failed: ${nextResponse.status}`;
                try {
                  const errBody = await nextResponse.text();
                  console.error('[mcp-chat-widget] Continuation error:', errBody);
                  errorDetail += ` - ${errBody}`;
                } catch { /* ignore */ }
                throw new Error(errorDetail);
              }

              // Recursively read the next stream (may have more tool calls)
              await this.readChatStream(nextResponse, turn, continuation, turn.clientRoundTripCount);
              resolve();
            } catch (err) {
              if (turn.abortReason === 'user' || turn.abortReason === 'timeout') {
                reject(err);
                return;
              }

              if (isWidgetAuthExpiredError(err)) {
                ts.removeMessage(assistantId);
                ts.setState({ isLoading: false });
                ts.requestInputFocus();
                reject(err);
                return;
              }

              const isRateLimited = (err as any)?.isRateLimited === true;
              const message = isRateLimited
                ? (t('rateLimited') || 'Please wait a moment before sending another message.')
                : (err instanceof Error ? err.message : String(err));
              this.cancelStreamingContentFlush(turn);
              ts.updateMessage(assistantId, {
                content: isRateLimited
                  ? (t('rateLimited') || 'Please wait a moment before sending another message.')
                  : accumulated + `\n\n_Tool execution failed: ${message}_`,
                isStreaming: false,
              });
              if (isRateLimited) {
                const lockMs = (err as any).retryAfterMs || 30_000;
                const captchaNeeded = (err as any).captchaRequired === true;
                ts.setState({
                  isLoading: false,
                  rateLimitLock: {
                    lockedUntil: Date.now() + lockMs,
                    captchaRequired: captchaNeeded,
                  },
                });
              } else {
                ts.setState({ isLoading: false });
                ts.requestInputFocus();
              }
              reject(err);
            }
            return;
          }

          // Normal completion or paused_for_confirmation
          if (accumulated) {
          }
          // No tool was ever selected this turn, so a "fill out the form" promise in the
          // prose can't be backed by a real form - normalize it. See normalizeMisleadingFormMention.
          const finalContent = (!toolUseOccurredThisTurn && pendingToolUses.length === 0)
            ? normalizeMisleadingFormMention(accumulated)
            : accumulated;
          turn.accumulatedText = finalContent;
          this.drivingResponseTracker.finalizeLastResponse(finalContent);
          this.cancelStreamingContentFlush(turn);
          ts.updateMessage(assistantId, {
            content: finalContent,
            isStreaming: false,
          });

          // Any terminal assistant reply should extend the canonical history.
          // The only exception is 'paused_for_confirmation': that's a mid-turn
          // pause waiting on the user, not a finished assistant turn. Recording
          // it here would store a tool_use-less assistant message that the later
          // confirmed round would append after, breaking the required
          // user/assistant alternation.
          if (reason !== 'paused_for_confirmation' && reason !== 'awaiting_tool_results') {
            const historyBase = conversationMessages ?? store.getState().historyMessages;
            const trimmedFinal = finalContent.trim();
            if (trimmedFinal) {
              setStructuredHistory([...historyBase, { role: 'assistant', content: finalContent }]);
            } else if (conversationMessages) {
              setStructuredHistory(historyBase);
            }
          }

          // Brief cooldown to prevent rapid-fire message abuse
          setTimeout(() => {
            ts.setState({ isLoading: false });
            ts.requestInputFocus();
          }, 2000);

          // Activate chart placeholders after the full re-render
          if (store.getState().boundTurnId === turnId) {
            requestAnimationFrame(() => {
              activateCharts(this.shadow, assistantId);
            });
          }

          resolve();
        },

        onError: (err) => {
          if (turn.abortReason === 'user' || turn.abortReason === 'timeout') {
            reject(err);
            return;
          }

          this.cancelStreamingContentFlush(turn);
          ts.updateMessage(assistantId, {
            content: this.buildAssistantErrorContent(
              err,
              t('chatError') || 'Sorry, an error occurred. Please try again.',
              turn.accumulatedText,
            ),
            isStreaming: false,
            turnError: { turnId },
          });
          ts.setState({ isLoading: false });
          ts.requestInputFocus();
          const errorDetail = (err as any)?.detail || null;
          (this.shadow.host as HTMLElement).dispatchEvent(
            new CustomEvent('mcp-chat:error', {
              bubbles: true,
              composed: true,
              detail: { turnId, error: err.message, technicalDetail: errorDetail },
            })
          );
          reject(err);
        },
      }, pollTarget).catch(reject);
    });
  }

  protected enrichToolInputFromContext(toolName: string, input: Record<string, unknown>): Record<string, unknown> {
    const toolDef = store.getState().tools.find(t => t.name === toolName);
    const properties = (toolDef?.inputSchema as Record<string, unknown> | undefined)?.properties as Record<string, Record<string, unknown>> | undefined;
    if (!properties) return input;

    const latestAttachment = this.getLatestAttachmentContext();
    const latestToolResult = this.getLatestToolResultObject();
    const enriched: Record<string, unknown> = { ...input };

    for (const [fieldName, prop] of Object.entries(properties)) {
      const current = enriched[fieldName];
      if (current !== undefined && current !== null && current !== '') continue;

      const inferred = this.inferFieldValue(fieldName, prop, latestAttachment, latestToolResult);
      if (inferred !== undefined) {
        enriched[fieldName] = inferred;
      }
    }

    return enriched;
  }

  /** The canonical structured LLM history - includes tool_use/tool_result blocks
   *  that the display-only `messages` (ChatMessage[]) collapses to plain text. */
  private getHistoryMessages(): LlmMessage[] {
    // Server is authoritative for LLM history capping — send full structured history.
    return store.getState().historyMessages;
  }
}

